import { storeToRefs } from "pinia";
import { useStudyUsers } from "@/store/study-users.store";
import { useQnAStore } from "@/experiences/qna/store/qna.store";
import { useContext } from "@/store/context";
import { useStudiesStore } from "@/store/studies";
import { MentionType } from "@/experiences/qna/types";
// Composable
export const useQuestionFormatting = () => {
    const usersStore = useStudyUsers();
    const { users } = storeToRefs(usersStore);
    const { allSegmentsMap } = storeToRefs(useQnAStore());
    const { currentStudyId } = storeToRefs(useContext());
    const { studies } = storeToRefs(useStudiesStore());
    /**
     * Normalizes whitespace in a string by reducing multiple consecutive newlines to a maximum of two (one blank line)
     * and trimming leading and trailing whitespace.
     *
     * @param str - The input string to normalize.
     * @returns The normalized string with excess newlines reduced and whitespace trimmed.
     */
    const normalizeWhitespace = (str) => {
        // Normalize multiple consecutive newlines to maximum of 2 (one blank line) and trim whitespace
        return str.replace(/\n{3,}/g, "\n\n").trim();
    };
    const htmlToQuestion = (str) => {
        let query = "";
        const parser = new DOMParser();
        const { body: input } = parser.parseFromString(str, "text/html");
        for (const node of input.childNodes) {
            if (node.nodeType === Node.TEXT_NODE) {
                // Append plain text directly
                query += node.textContent;
            }
            else if (node.nodeType === Node.ELEMENT_NODE &&
                node.tagName === "SPAN" &&
                node.classList.contains("qna-mentions__mention")) {
                // Convert span elements to ai queries
                const span = node;
                const { mentionKey } = span.dataset;
                const [type, id] = (mentionKey ?? "").split("-");
                query += `@${type}${id}`;
            }
            else if (node.nodeType === Node.ELEMENT_NODE) {
                query += node.outerHTML;
            }
        }
        return normalizeWhitespace(query);
    };
    const encapsulateMention = (mention, attrs) => {
        const { type, text, value } = mention;
        const element = document.createElement(attrs.tag);
        if (attrs.tag.toLowerCase() === "a" && !attrs.href) {
            throw new Error("href is required for anchor tags");
        }
        else if (attrs.tag.toLowerCase() === "a") {
            element.href = attrs.href;
            element.setAttribute("target", "_blank");
        }
        element.textContent = text;
        element.contentEditable = "false";
        element.classList.add(...attrs.class.split(" "));
        // Position-independent key format: type-value (matches QnAMentions.vue getMentionKey)
        element.dataset.mentionKey = `${type}-${value}`;
        return element.outerHTML;
    };
    /**
     * Converts a question string to HTML, replacing user and segment mentions with appropriate HTML elements.
     *
     * @param str - The question string to format.
     * @param opts - Optional formatting options.
     * @param opts.onlySpans - If true, mentions are rendered as <span> elements instead of <a> links.
     * @param opts.profileCards - If true, adds profile card styling to user mentions.
     * @param opts.studyId - The study ID to use for resolving user mentions.
     * @returns The formatted HTML string with mentions replaced.
     */
    const questionToHtml = (str, opts) => {
        const normalized = str.trim();
        const withLineBreaks = normalized.replace(/\n/g, "<br>");
        return withLineBreaks.replace(/@(segment|user)(\d+)/g, (match, type, id) => {
            const value = Number(id);
            const resource = type === MentionType.User
                ? users.value[value] || usersStore.loadByUserId(value, opts?.studyId ?? currentStudyId.value)
                : allSegmentsMap.value.get(value);
            if (!resource) {
                return match;
            }
            let studyAlias;
            if (!opts?.onlySpans && type === MentionType.User) {
                const targetStudyId = opts?.studyId ?? currentStudyId.value;
                studyAlias = studies.value.find((s) => s.studyId === targetStudyId)?.alias;
            }
            const text = type === MentionType.User ? resource.displayName : resource.name;
            const mention = { text, type, value };
            const attrs = {
                tag: !opts?.onlySpans && studyAlias && type === MentionType.User ? "a" : "span",
                href: !opts?.onlySpans && studyAlias && type === MentionType.User ? `/${studyAlias}/user/${value}` : undefined,
                class: `qna-mentions__mention${opts?.profileCards && type === MentionType.User ? " rc-profile-card" : ""}`
            };
            return encapsulateMention(mention, attrs);
        });
    };
    /**
     * Truncates a question string for display by limiting it to the first two paragraphs.
     * If there are more than two paragraphs, an ellipsis ("...") is appended.
     *
     * @param str - The question string to truncate.
     * @returns The truncated question string suitable for display.
     */
    const truncateQuestionForDisplay = (str) => {
        const normalized = str.trim();
        // Split into paragraphs (separated by blank lines)
        const paragraphs = normalized.split(/\n\n+/).filter((p) => p.trim());
        if (paragraphs.length <= 1) {
            // Single paragraph - remove any internal blank lines
            return paragraphs[0] || "";
        }
        return paragraphs.slice(0, 2).join("\n") + (paragraphs.length > 2 ? "..." : "");
    };
    return {
        questionToHtml,
        htmlToQuestion,
        normalizeWhitespace,
        truncateQuestionForDisplay
    };
};
